import usePrivateAxios from "@/common/api/usePrivateAxios"
import { useMutation } from "@tanstack/react-query"
import { AxiosResponse } from "axios"
import { unFormatUnit } from "@/common/utils/unFormatters"
import { mapToDeletion, reconcileRailsUpdate } from "@/common/utils/railsHelpers"
import { getRailsUpdate } from "@/common/utils/getRailsUpdate"
import { WorkflowScheduleData } from "../types/workflowSchedules.type"
import { CreateWorkflowSchedulesSchema } from "../services/workflowSchedules.schema"

interface UseUpdateMaintenanceWorkflowScheduleProps {
  id?: string
  workflowSchedule?: WorkflowScheduleData
  onSuccess?: (response: AxiosResponse) => void
  onError?: (error: any) => void
}

const useUpdateMaintenanceWorkflowSchedule = ({ id, workflowSchedule, onSuccess, onError }: UseUpdateMaintenanceWorkflowScheduleProps) => {
  const privateAxios = usePrivateAxios()
  return useMutation({
    mutationKey: ['updateMaintenanceWorkflowSchedule'],
    mutationFn: async (form: CreateWorkflowSchedulesSchema) => {
      const hasKilometers = form.execution_options?.some((option) => option.value === 'kilometers_usage')
      const hasDays = form.execution_options?.some((option) => option.value === 'days')
      const hasHours = form.execution_options?.some((option) => option.value === 'hours_usage')

      const workflow_schedule = {
        display_name: form.display_name,
        kind: form.kind?.value,
        observations: form.observations || undefined,
        equipment_category_ids: form.equipment_category_ids ? [form.equipment_category_ids.value] : undefined,
        branch_ids: form.branch_ids ? form.branch_ids.map((item) => item.value) : undefined,
        manufacturer_ids: form.manufacturer_ids ? form.manufacturer_ids.map((item) => item.value) : undefined,
        equipment_model_ids: form.equipment_model_ids ? form.equipment_model_ids.map((item) => item.value) : undefined,
        equipment_ids: form.equipment_ids ? form.equipment_ids.map((item) => item.value) : undefined,
        interval_in_kilometers_usage: (form.interval_in_kilometers_usage && hasKilometers) ? unFormatUnit(form.interval_in_kilometers_usage, 'Km') : undefined,
        interval_in_days: (form.interval_in_days && hasDays) ? unFormatUnit(form.interval_in_days, 'Dias') : undefined,
        interval_in_hours_usage: (form.interval_in_hours_usage && hasHours) ? unFormatUnit(form.interval_in_hours_usage, 'H') : undefined,
        objectives_attributes: reconcileRailsUpdate(
          getRailsUpdate(
            workflowSchedule?.objectives || [],
            form.objectives_attributes || [],
            (x) => x.id || '',
            (x) => x.id || '',
            mapToDeletion,
            (newObjective) => ({
              system_id: newObjective.system_id.value,
              part_id: newObjective.part_id.value,
              action_id: newObjective.action_id.value,
            }),
          ),
          (destroyed) => [destroyed.system.id, destroyed.part.id, destroyed.action.id],
          (created) => [created.system_id, created.part_id, created.action_id],
        )
      }

      return privateAxios.put(`/maintenance/workflow_schedules/${id}`, { workflow_schedule })
    },
    onSuccess,
    onError,
  })
}

export default useUpdateMaintenanceWorkflowSchedule

